import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const Shop = () => {
  // --- State ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- API URLs ---
  const API_URL = `${process.env.REACT_APP_BACKEND}/api/products`;
  const CATEGORY_API_URL = `${process.env.REACT_APP_BACKEND}/api/categories`;

  // --- Fetch Data ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(API_URL),
          axios.get(CATEGORY_API_URL),
        ]);

        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setError("");
      } catch (err) {
        setError("Failed to load shop data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, CATEGORY_API_URL]);

  // --- Filter Logic ---
  const filteredProducts =
    activeCategory === "ALL"
      ? products
      : products.filter((p) => p.category_id === activeCategory);

  return (
    <div className="page">
      {/* Hide Webkit Scrollbar */}
      <style>{`
        .hide-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Header */}
      <div className="eyebrow" style={{ textAlign: "center" }}>
        THE COLLECTION
      </div>
      <div className="section-title">Shop BeeLuxe</div>
      <p className="section-sub">
        Small-batch honey and hive goods, bottled within 48 hours of harvest.
      </p>

      {/* Category Filter Scrollbar */}
      <div
        className="hide-scroll"
        style={{
          display: "flex",
          overflowX: "auto",
          gap: "12px",
          padding: "0 20px 10px 20px",
          marginBottom: "32px",
          justifyContent: "flex-start",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <button
          onClick={() => setActiveCategory("ALL")}
          style={{
            padding: "8px 20px",
            borderRadius: "20px",
            border: "1px solid #ccc",
            background: activeCategory === "ALL" ? "#333" : "transparent",
            color: activeCategory === "ALL" ? "#fff" : "inherit",
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontFamily: "inherit",
          }}
        >
          All Products
        </button>

        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            style={{
              padding: "8px 20px",
              borderRadius: "20px",
              border: "1px solid #ccc",
              background: activeCategory === cat.id ? "#333" : "transparent",
              color: activeCategory === cat.id ? "#fff" : "inherit",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
            }}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Status Messages */}
      {loading && <p style={{ textAlign: "center" }}>Loading collection...</p>}
      {error && <p style={{ textAlign: "center", color: "red" }}>{error}</p>}

      {/* Product Grid */}
      {!loading && !error && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "16px",
            padding: "0 20px",
          }}
        >
          {filteredProducts.length === 0 ? (
            <p style={{ gridColumn: "1 / -1", textAlign: "center" }}>
              No products found in this category.
            </p>
          ) : (
            filteredProducts.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                }}
              >
                <article
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
                    overflow: "hidden",
                    height: "140px",
                    width: "100%",
                  }}
                >
                  {/* Left Thumbnail Section */}
                  <div
                    style={{
                      width: "125px",
                      minWidth: "125px",
                      height: "100%",
                      backgroundColor: "#f9fafb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {p.image ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND}/uploads/${p.image}`}
                        alt={p.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML =
                            '<div style="font-size: 2.2rem;">🍯</div>';
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: "2.2rem" }}>🍯</div>
                    )}
                  </div>

                  {/* Right Content Section */}
                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          color: "#111827",
                          lineHeight: "1.25",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: "break-word",
                        }}
                      >
                        {p.name}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "1.25",
                        }}
                      >
                        {p.description || "Premium BeeLuxe product."}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: "700",
                        color: "#111827",
                        marginTop: "4px",
                      }}
                    >
                      ${Number(p.price).toFixed(2)}
                    </div>
                  </div>
                </article>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Shop;
